import React, { useState, useEffect, useMemo, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import ComparatorComponent from './ComparatorComponent';
import DisclaimerComponent from 'features/insuranceDocument/edit/general/components/DisclaimerComponent';
import { QUOTATIONS_QUERY } from '../queries';
import { HAS_OFFERS_HISTORY_QUERY } from 'features/insuranceDocument/edit/general/offersHistory/queries';
import { SET_OFFER_DETAIL_ON_DOCUMENT, COMPUTE_RCA_QUOTATION } from '../mutations';
import {
  QUOTATION_INITIALIZED,
  QUOTATION_COMPUTED,
  QUOTATIONS_INITIALIZED,
  RCA_QUOTATION_DATA_CHANGED,
  DOCUMENT_OFFER_DETAIL_CHANGED
} from '../subscriptions';
import { QUOTATIONS_COMPUTED } from 'features/insuranceDocument/edit/general/actions/subscriptions';
import { useMutation } from 'features/common/security/hooks';
import { useReactiveVar, useSubscription } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import { QuotationsStatus } from 'constants/defaultValues';
import {
  useSimpleError,
  useQueryWithSimpleErrorHandling,
  useClientQueryWithSimpleErrorHandling,
  useParams as useBehaviorParams,
  useBackdrop,
  useAuthorizations
} from 'hooks';
import HistoryAnchorComponent from '../../HistoryAnchorComponent';
import { Grid } from '@mui/material';
import Replay from '@mui/icons-material/Replay';
import { useTranslation } from 'react-i18next';
import { IconButton } from '@totalsoft/rocket-ui';
import { quotationsStatusVar } from 'features/insuranceDocument/variables';
import InsuranceDataContext from 'features/insuranceDocument/stepper/insuranceDataContext';
import AuthorizationCodes from 'features/common/constants/authorizationCodes';

const ComparatorContainer = ({ documentId, currentStateCode, recomputeQuotations, disableRecompute }) => {
  const { documentTypeId, documentStateId } = useContext(InsuranceDataContext);
  const authorizations = useAuthorizations(
    [AuthorizationCodes.hasRightToRecomputeQuotations, AuthorizationCodes.hasRightToViewOffersHistory],
    documentTypeId,
    documentStateId
  );
  const history = useHistory();
  const { t } = useTranslation();
  const clientQuery = useClientQueryWithSimpleErrorHandling();
  const showError = useSimpleError();

  const [hasOffersHistory, setHasOffersHistory] = useState(false);
  const [quotations, setQuotations] = useState([]);
  const status = useReactiveVar(quotationsStatusVar);
  const [loading, setLoading] = useState(true);
  const { Backdrop, showBackdrop, hideBackdrop } = useBackdrop();
  const [prevSelectedDetailId, setPrevSelectedDetailId] = useState(null);

  const offerInNegotiationState = useBehaviorParams('OfferInNegotiationDocumentStateCode');
  const offerInNegotiation = useMemo(() => currentStateCode === offerInNegotiationState, [currentStateCode, offerInNegotiationState]);

  // #region Queries

  const { refetch } = useQueryWithSimpleErrorHandling(HAS_OFFERS_HISTORY_QUERY, {
    variables: { documentId },
    onCompleted: data => {
      setHasOffersHistory(data.hasOffersHistory);
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true
  });

  useEffect(() => {
    if (!status || !documentId) return;
    showBackdrop();
    async function fetchQuotations() {
      const { data } = await clientQuery(QUOTATIONS_QUERY, {
        variables: { documentId },
        notifyOnNetworkStatusChange: true
      });

      if (data && data.quotations.quotations) {
        hideBackdrop();
        setQuotations(data.quotations.quotations);
        setLoading(false);
      }
    }
    if (status === QuotationsStatus.VALID || status === QuotationsStatus.PROCESSING || status === QuotationsStatus.EXPIRED) {
      fetchQuotations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, documentId]);

  // #endregion

  // #region Set detail

  const setSelectedQuotation = useCallback(offerDetailId => {
    setQuotations(prev =>
      prev.map(quotation => {
        return !quotation.details
          ? quotation
          : Object.assign({}, quotation, {
              details: [
                ...quotation.details.map(detail => {
                  return detail.offerDetailId === offerDetailId ? { ...detail, selected: true } : { ...detail, selected: false };
                })
              ]
            });
      })
    );
  }, []);

  const [setOfferDetailOnDocument] = useMutation(SET_OFFER_DETAIL_ON_DOCUMENT, {
    onError: error => {
      setSelectedQuotation(prevSelectedDetailId);
      hideBackdrop();
      setLoading(false);
      showError(error);
    }
  });

  const handleSelectQuotation = useCallback(
    offerDetailId => {
      showBackdrop();

      quotations.forEach(quotation =>
        quotation.details.forEach(detail => detail.selected === true && setPrevSelectedDetailId(detail.offerDetailId))
      );
      setSelectedQuotation(offerDetailId);

      const input = { documentId, offerDetailId, validateInsuranceAmount: true };
      setOfferDetailOnDocument({
        variables: {
          input
        }
      });
    },
    [documentId, quotations, setSelectedQuotation, setOfferDetailOnDocument, showBackdrop]
  );

  // #endregion

  // #region Retry quotations

  const [computeQuotation] = useMutation(COMPUTE_RCA_QUOTATION, { onError: error => showError(error) });

  const handleRetryQuotation = useCallback(
    (insuranceOfferId, insurerId, period) => {
      const input = { documentId, insuranceOfferId, insurerId, period };
      const mutationKey = `${insurerId}-${period}`;
      computeQuotation({
        mutationKey,
        variables: {
          input
        }
      });
    },
    [documentId, computeQuotation]
  );

  // #endregion

  // #region Subscriptions

  useSubscription(QUOTATION_INITIALIZED, {
    variables: { documentId },
    skip: !documentId,
    onData: ({ data: { data } }) => {
      setQuotations(
        quotations.map(quotation => {
          return quotation.offerId !== data?.quotationInitialized?.quotation.offerId ? quotation : data?.quotationInitialized?.quotation;
        })
      );
    }
  });

  useSubscription(QUOTATION_COMPUTED, {
    variables: { documentId },
    skip: !documentId,
    onData: ({ data: { data } }) => {
      setQuotations(
        quotations.map(quotation => {
          return quotation.offerId === data.quotationComputed.quotation.offerId ? data.quotationComputed.quotation : quotation;
        })
      );
      refetch({ documentId });
    }
  });

  useSubscription(QUOTATIONS_COMPUTED, {
    variables: { documentId },
    skip: !documentId,
    onData: _ => {
      /* Subscribe to finish global loading (check apollo/config.js for corresponding mutation(s)) */
    }
  });

  useSubscription(DOCUMENT_OFFER_DETAIL_CHANGED, {
    variables: { documentId },
    skip: !documentId,
    onData: _ => {
      /* Subscribe to finish global loading (check apollo/config.js for corresponding mutation(s)) */
    }
  });

  useSubscription(QUOTATIONS_INITIALIZED, {
    variables: { documentId },
    skip: !documentId,
    onData: ({ data: { data } }) => {
      setLoading(false);
      hideBackdrop();
      setQuotations(data.quotationsInitialized.quotationData.quotations);
      refetch({ documentId });
    }
  });

  useSubscription(RCA_QUOTATION_DATA_CHANGED, {
    variables: { documentId },
    skip: !documentId,
    onData: ({
      data: {
        data: { rcaQuotationDataChanged }
      }
    }) => {
      setQuotations(rcaQuotationDataChanged.quotationData.quotations);
    }
  });

  // #endregion

  const handleClickOffersHistoryList = useCallback(
    () => history.push(`/insurance-documents/${documentId}/offers-history`),
    [documentId, history]
  );

  const sortQuotations = useCallback(() => {
    return quotations.slice().sort((a, b) => {
      if (a.period < b.period) return -1;
      if (a.period > b.period) return 1;
      if (a.insurerName < b.insurerName) return -1;
      return 1;
    });
  }, [quotations]);

  const quotationsByPeriod = useMemo(() => {
    const sortedQuotations = sortQuotations();

    const result = sortedQuotations.reduce((acc, curr) => {
      if (!acc[curr.period]) acc[curr.period] = [];
      acc[curr.period].push(curr);
      return acc;
    }, []);

    return result;
  }, [sortQuotations]);

  const computing = useMemo(() => {
    if (!status) return false;
    return status !== QuotationsStatus.VALID;
  }, [status]);

  return (
    <>
      {authorizations?.hasRightToRecomputeQuotations && (
        <Grid item container justifyContent={'flex-end'} pr={1}>
          <IconButton
            color='primary'
            disabled={disableRecompute}
            onClick={recomputeQuotations}
            tooltip={t('Offer.Edit.Actions.RecomputeQuotations')}
          >
            <Replay />
          </IconButton>
        </Grid>
      )}
      {quotationsByPeriod &&
        quotationsByPeriod.map((list, period) => (
          <ComparatorComponent
            loading={loading}
            data={list}
            key={period}
            onSelectQuotation={handleSelectQuotation}
            onRetryQuotation={handleRetryQuotation}
            computing={computing}
            offerInNegotiation={offerInNegotiation}
          />
        ))}
      {hasOffersHistory && authorizations?.hasRightToViewOffersHistory && <HistoryAnchorComponent onClick={handleClickOffersHistoryList} />}
      {quotations && quotations.length > 0 && <DisclaimerComponent />}
      <Backdrop />
    </>
  );
};

ComparatorContainer.propTypes = {
  documentId: PropTypes.number.isRequired,
  currentStateCode: PropTypes.string.isRequired,
  disableRecompute: PropTypes.bool.isRequired,
  recomputeQuotations: PropTypes.func.isRequired
};

export default ComparatorContainer;
